<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    nextTick,
    onBeforeUnmount,
    onMounted,
    ref,
    watch,
  } from 'vue';
  import type { ICanvasPageHandle } from '@/components/v2/layout/canvas-page';
  import V2CanvasPage from '@/components/v2/layout/V2CanvasPage.vue';
  import V2CatalogEditorHost from '@/components/v2/catalog/editor/V2CatalogEditorHost.vue';
  import V2CatalogCommandBar from '@/components/v2/catalog/head/V2CatalogCommandBar.vue';
  import V2CatalogList from '@/components/v2/catalog/list/V2CatalogList.vue';
  import V2CatalogChipNav from '@/components/v2/catalog/nav/V2CatalogChipNav.vue';
  import V2CatalogRail from '@/components/v2/catalog/nav/V2CatalogRail.vue';
  import V2CatalogRowPanels from '@/components/v2/catalog/panels/V2CatalogRowPanels.vue';
  import V2CatalogReorderPanel from '@/components/v2/catalog/reorder/V2CatalogReorderPanel.vue';
  import V2CatalogSelectionBar from '@/components/v2/catalog/select/V2CatalogSelectionBar.vue';
  import V2CatalogEmptyStore from '@/components/v2/catalog/states/V2CatalogEmptyStore.vue';
  import V2CatalogLoadError from '@/components/v2/catalog/states/V2CatalogLoadError.vue';
  import V2CatalogLoading from '@/components/v2/catalog/states/V2CatalogLoading.vue';
  import V2CatalogNoResults from '@/components/v2/catalog/states/V2CatalogNoResults.vue';
  import { useCatalogPageSetup } from '@/composition/catalog/use-catalog-page-setup';
  import { MENU_UPDATE_UNDONE_EVENT } from '@/domain/menu-import/update-undo';
  import { EventBus } from '@/modules/EventBus';

  /**
   * The «Menú» page (plan §5): categories and products on one canvas page,
   * with a sticky head, the rail (tablet and desktop) or category chips
   * (phones), the windowed list and the page's single floating panel.
   */
  performance.mark('menuV2:setup');

  const vm = getCurrentInstance()!.proxy;
  const canvas = ref<ICanvasPageHandle | null>(null);
  const { state, layout, data, actions, commands } =
    useCatalogPageSetup(canvas);

  const tier = computed(() => layout.tier.value);
  const columns = computed(() => layout.columns.value);
  const loadStatus = computed(() => data.loadStatus.value);
  const ready = computed(() => loadStatus.value === 'ready');
  /** No category and no product in the current menu. */
  const empty = computed(() => ready.value && !data.sections.value.length);
  const noResults = computed(
    () => ready.value && !empty.value && !data.shownSections.value.length
  );
  const showNav = computed(
    () => ready.value && !empty.value && state.mode !== 'reorder'
  );
  /** Read out while the assistant's highlight shows («Ver en el menú»). */
  const highlightAnnouncement = computed(() => {
    const count = Object.keys(state.highlightIds).length;
    return count
      ? String(vm.$tc('assistant.highlight.announce', count, { n: count }))
      : '';
  });

  // Find mode exists only on phones.
  watch(tier, (next) => {
    if (next !== 'phone' && state.mode === 'find') state.mode = 'browse';
  });

  //#region Telemetry

  let opened = false;
  watch(
    ready,
    async (isReady) => {
      if (!isReady || opened) return;
      opened = true;
      const label = `open:${tier.value}` as const;
      await nextTick();
      window.requestAnimationFrame(() => {
        performance.mark('menuV2:first-list-paint');
        // Analytics after the first paint, never before it.
        window.setTimeout(() => actions.track(label));
      });
    },
    { immediate: true }
  );

  //#endregion

  function onMenuUpdateUndone() {
    data.reloadCatalog().catch(() => undefined);
  }

  onMounted(() => EventBus.$on(MENU_UPDATE_UNDONE_EVENT, onMenuUpdateUndone));
  onBeforeUnmount(() =>
    EventBus.$off(MENU_UPDATE_UNDONE_EVENT, onMenuUpdateUndone)
  );

  function closeReorder() {
    void commands.closeReorder();
  }
</script>

<script lang="ts">
  import type { NavigationGuardNext, Route } from 'vue-router';
  import {
    confirmCatalogEditorDiscard,
    confirmCatalogLeave,
  } from '@/composition/catalog/use-catalog-leave-guard';
  import { menuV2EnabledFor } from '@/composition/use-menu-v2';

  /** The editor the URL opens (`item` / `category`, and `cId` of a create). */
  function editorKey(route: Route): string {
    const { item, category, cId } = route.query;
    return JSON.stringify([
      item ?? null,
      category ?? null,
      item ? cId ?? null : null,
    ]);
  }

  // vue-router 3 has no composable guards: the in-component ones are options,
  // and they reach the page through use-catalog-leave-guard.
  export default {
    async beforeRouteLeave(to: Route, from: Route, next: NavigationGuardNext) {
      next((await confirmCatalogLeave()) ? undefined : false);
    },
    async beforeRouteUpdate(to: Route, from: Route, next: NavigationGuardNext) {
      const stringId = to.params.restaurantId;
      if (stringId !== from.params.restaurantId) {
        // The reused-page guard already asked; `beforeEnter` does not run
        // when only the store changes (§5.1), so the flag is checked here.
        if (!menuV2EnabledFor(stringId))
          next({ path: `/${stringId}/items`, replace: true });
        else next();
        return;
      }
      // Only the editor changes: an unsaved order stays on screen, unasked.
      if (
        editorKey(to) !== editorKey(from) &&
        !(await confirmCatalogEditorDiscard())
      ) {
        next(false);
        return;
      }
      next();
    },
  };
</script>

<template>
  <div class="v2-catalog-page">
    <V2CanvasPage
      ref="canvas"
      :rail-width="showNav ? columns.railWidth : null"
      :aside-width="columns.asideWidth"
      :aside-docked="columns.asideDocked"
      :aside-open="!!state.editor"
      :gutter="columns.gutter"
      :bottom-pad="columns.bottomPad"
      :head-surface="tier === 'phone'"
      @resize="layout.onCanvasResize"
    >
      <template #head>
        <V2CatalogCommandBar
          :empty="!ready || empty"
          @close-reorder="closeReorder"
        />
        <V2CatalogChipNav
          v-if="tier === 'phone' && showNav && state.mode !== 'find'"
        />
      </template>

      <template v-if="showNav && columns.railWidth" #rail>
        <V2CatalogRail />
      </template>

      <V2CatalogLoadError v-if="loadStatus === 'error'" />
      <V2CatalogLoading v-else-if="!ready" />
      <V2CatalogEmptyStore v-else-if="empty" />
      <V2CatalogReorderPanel v-else-if="state.mode === 'reorder'" />
      <V2CatalogNoResults v-else-if="noResults" />
      <V2CatalogList v-else />

      <template #aside>
        <V2CatalogEditorHost />
      </template>
    </V2CanvasPage>

    <V2CatalogRowPanels />
    <V2CatalogSelectionBar />
    <p class="page-announcer" aria-live="polite">
      {{ highlightAnnouncement }}
    </p>
  </div>
</template>

<style scoped lang="scss">
  .v2-catalog-page {
    @apply tw-flex tw-min-h-0 tw-flex-1 tw-flex-col;

    .page-announcer {
      @apply tw-sr-only;
    }
  }
</style>
